import { createBrowserRouter, redirect } from 'react-router';
import { lazy } from 'react';
import { AppShell } from './components/layout/AppShell.tsx';
import { ProjectsLayout } from './components/layout/ProjectsLayout.tsx';
import { ServersLayout } from './components/layout/ServersLayout.tsx';
import { SettingsLayout } from './components/layout/SettingsLayout.tsx';
import { PROJECTS_HUB_PATHS, SERVERS_HUB_PATHS, SETTINGS_PATHS } from './lib/nav-paths.ts';
import { isCustomStatusHost } from './lib/public-status-host.ts';
import { useAuthStore } from './stores/auth-store.ts';

const LoginPage = lazy(() => import('./pages/LoginPage.tsx'));
const SetupPage = lazy(() => import('./pages/SetupPage.tsx'));
const OAuthCallbackPage = lazy(() => import('./pages/OAuthCallbackPage.tsx'));
const DashboardPage = lazy(() => import('./pages/DashboardPage.tsx'));
const GitHubReposPage = lazy(() => import('./pages/GitHubReposPage.tsx'));
const SecretsPage = lazy(() => import('./pages/SecretsPage.tsx'));
const WorkflowPage = lazy(() => import('./pages/WorkflowPage.tsx'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage.tsx'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage.tsx'));
const ProjectCreatePage = lazy(() => import('./pages/ProjectCreatePage.tsx'));
const ProjectEditPage = lazy(() => import('./pages/ProjectEditPage.tsx'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage.tsx'));
const ServersPage = lazy(() => import('./pages/ServersPage.tsx'));
const ServerCreatePage = lazy(() => import('./pages/ServerCreatePage.tsx'));
const ServerEditPage = lazy(() => import('./pages/ServerEditPage.tsx'));
const RuntimeOverviewPage = lazy(() => import('./pages/RuntimeOverviewPage.tsx'));
const ServerDetailPage = lazy(() => import('./pages/ServerDetailPage.tsx'));
const TeamsPage = lazy(() => import('./pages/TeamsPage.tsx'));
const DeploymentsPage = lazy(() => import('./pages/DeploymentsPage.tsx'));
const ProjectRunsPage = lazy(() => import('./pages/ProjectRunsPage.tsx'));
const DeploymentDetailPage = lazy(() => import('./pages/DeploymentDetailPage.tsx'));
const SshKeysPage = lazy(() => import('./pages/SshKeysPage.tsx'));
const SettingsPage = lazy(() => import('./pages/SettingsPage.tsx'));
const AIDrivenPage = lazy(() => import('./pages/AIDrivenPage.tsx'));
const McpActivityPage = lazy(() => import('./pages/McpActivityPage.tsx'));
const ServicesPage = lazy(() => import('./pages/ServicesPage.tsx'));
const SitesPage = lazy(() => import('./pages/SitesPage.tsx'));
const SiteCreatePage = lazy(() => import('./pages/SiteCreatePage.tsx'));
const SiteEditPage = lazy(() => import('./pages/SiteEditPage.tsx'));
const DomainCertificatesPage = lazy(() => import('./pages/DomainCertificatesPage.tsx'));
const OpsTimelinePage = lazy(() => import('./pages/OpsTimelinePage.tsx'));
const EnvironmentCreatePage = lazy(() => import('./pages/EnvironmentCreatePage.tsx'));
const EnvironmentDetailPage = lazy(() => import('./pages/EnvironmentDetailPage.tsx'));
const ReleasePlanCreatePage = lazy(() => import('./pages/ReleasePlanCreatePage.tsx'));
const LogQueryPage = lazy(() => import('./pages/LogQueryPage.tsx'));
const CloudflarePage = lazy(() => import('./pages/CloudflarePage.tsx'));
const PipelineListPage = lazy(() => import('./pages/PipelineListPage.tsx'));
const PipelineDetailPage = lazy(() => import('./pages/PipelineDetailPage.tsx'));
const PipelineRunDetailPage = lazy(() => import('./pages/PipelineRunDetailPage.tsx'));
const PublicStatusPage = lazy(() => import('./pages/PublicStatusPage.tsx'));
const PublicStatusManagementPage = lazy(() => import('./pages/PublicStatusManagementPage.tsx'));

function authGuardLoader() {
  const accessToken = useAuthStore.getState().accessToken;
  if (!accessToken) return redirect('/login');
  return null;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    loader: authGuardLoader,
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: 'runtime',
        loader: () => redirect(SERVERS_HUB_PATHS.runtime),
      },
      {
        path: 'services',
        loader: () => redirect(SERVERS_HUB_PATHS.services),
      },
      {
        path: 'sites',
        loader: () => redirect(SERVERS_HUB_PATHS.sites),
      },
      {
        path: 'secrets',
        element: <SecretsPage />,
      },
      {
        path: 'workflows/:owner/:repo',
        element: <WorkflowPage />,
      },
      {
        path: 'workflows/:owner/:repo/:filename',
        element: <WorkflowPage />,
      },
      {
        path: 'notifications',
        element: <NotificationsPage />,
      },
      {
        path: 'projects',
        element: <ProjectsLayout />,
        children: [
          {
            index: true,
            element: <ProjectsPage />,
          },
          {
            path: 'repos',
            element: <GitHubReposPage />,
          },
          {
            path: 'releases',
            element: <DeploymentsPage />,
          },
          {
            path: 'runs',
            element: <ProjectRunsPage />,
          },
          {
            path: 'deployments',
            loader: () => redirect(PROJECTS_HUB_PATHS.releases),
          },
          {
            path: 'deployments/pipelines',
            loader: () => redirect(PROJECTS_HUB_PATHS.releases),
          },
          {
            path: 'deployments/runs',
            loader: () => redirect(`${PROJECTS_HUB_PATHS.runs}?type=deployment`),
          },
          {
            path: 'release-runs',
            loader: () => redirect(`${PROJECTS_HUB_PATHS.runs}?type=release`),
          },
          {
            path: 'new',
            element: <ProjectCreatePage />,
          },
          {
            path: ':projectId/edit',
            element: <ProjectEditPage />,
          },
          {
            path: ':projectId',
            element: <ProjectDetailPage />,
          },
          {
            path: ':projectId/environments/new',
            element: <EnvironmentCreatePage />,
          },
          {
            path: ':projectId/environments/:envId',
            element: <EnvironmentDetailPage />,
          },
          {
            path: ':projectId/release-plans/new',
            element: <ReleasePlanCreatePage />,
          },
          {
            path: ':projectId/pipelines',
            element: <PipelineListPage />,
          },
          {
            path: ':projectId/pipelines/:pipelineId',
            element: <PipelineDetailPage />,
          },
        ],
      },
      {
        path: 'servers',
        element: <ServersLayout />,
        children: [
          {
            index: true,
            element: <ServersPage />,
          },
          {
            path: 'new',
            element: <ServerCreatePage />,
          },
          {
            path: 'runtime',
            element: <RuntimeOverviewPage />,
          },
          {
            path: 'services',
            element: <ServicesPage />,
          },
          {
            path: 'sites',
            children: [
              { index: true, element: <SitesPage /> },
              { path: 'new', element: <SiteCreatePage /> },
              { path: ':id/edit', element: <SiteEditPage /> },
            ],
          },
          {
            path: 'domain-certificates',
            element: <DomainCertificatesPage />,
          },
          {
            path: 'timeline',
            element: <OpsTimelinePage />,
          },
          {
            path: ':id/edit',
            element: <ServerEditPage />,
          },
          {
            path: ':id',
            element: <ServerDetailPage />,
          },
        ],
      },
      {
        path: 'public-status',
        element: <PublicStatusManagementPage />,
      },
      {
        path: 'ai-driven',
        element: <AIDrivenPage />,
      },
      {
        path: 'mcp-activity',
        element: <McpActivityPage />,
      },
      {
        path: 'settings',
        element: <SettingsLayout />,
        children: [
          {
            index: true,
            element: <SettingsPage />,
          },
          {
            path: 'ssh-keys',
            element: <SshKeysPage />,
          },
          {
            path: 'teams',
            element: <TeamsPage />,
          },
          {
            path: 'cloudflare',
            element: <CloudflarePage />,
          },
        ],
      },
      {
        path: 'ssh-keys',
        loader: () => redirect(SETTINGS_PATHS.sshKeys),
      },
      {
        path: 'teams',
        loader: () => redirect(SETTINGS_PATHS.teams),
      },
      {
        path: 'cloudflare',
        loader: () => redirect(SETTINGS_PATHS.cloudflare),
      },
      {
        path: 'deployments',
        loader: () => redirect(PROJECTS_HUB_PATHS.releases),
      },
      {
        path: 'deployments/pipelines',
        loader: () => redirect(PROJECTS_HUB_PATHS.releases),
      },
      {
        path: 'deployments/runs',
        loader: () => redirect(`${PROJECTS_HUB_PATHS.runs}?type=deployment`),
      },
      {
        path: 'deployments/runs/:deploymentId',
        element: <DeploymentDetailPage />,
      },
      {
        path: 'deployments/:deploymentId',
        loader: ({ params }) => redirect(`/deployments/runs/${params.deploymentId}`),
        element: null,
      },
      {
        path: 'pipeline-runs',
        loader: () => redirect(`${PROJECTS_HUB_PATHS.runs}?type=release`),
      },
      {
        path: 'pipeline-runs/:runId',
        element: <PipelineRunDetailPage />,
      },
      {
        path: 'logs',
        element: <LogQueryPage />,
      },
    ],
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/setup',
    element: <SetupPage />,
  },
  {
    path: '/auth/callback',
    element: <OAuthCallbackPage />,
  },
  // Public no-login status page (singleton default)
  {
    path: '/status',
    element: <PublicStatusPage />,
  },
  // Legacy /status/:slug still works (slug ignored if not default)
  {
    path: '/status/:slug',
    element: <PublicStatusPage />,
  },
  // Custom domain / subdomain: entire origin is the status page (Host → by-host API)
  ...(isCustomStatusHost()
    ? [
        {
          path: '*',
          element: <PublicStatusPage />,
        },
      ]
    : []),
]);
